import { Ionicons } from '@expo/vector-icons';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { DateTimePickerModal } from '@/components/calendar/event-modal/date-time-picker';
import { SimpleAvatar } from '@/components/shared/SimpleAvatar';
import type { FamilyMember } from '@/types/family-member';
import type { RouteStopCandidate } from '@/lib/route-plan/RouteStopCandidateBuilder';
import type { RouteAsyncStatus } from '@/hooks/use-route-planner';

type TimeWindowOption = {
  label: string;
  start: string | null;
  end: string | null;
};

const TIME_WINDOWS: TimeWindowOption[] = [
  { label: 'All day', start: null, end: null },
  { label: 'Morning', start: null, end: '12:00' },
  { label: 'Afternoon', start: '12:00', end: '18:00' },
  { label: 'Evening', start: '18:00', end: null },
];

type StopPickerStepProps = {
  date: string;
  onDateChange: (date: string) => void;
  windowStart: string | null;
  windowEnd: string | null;
  onWindowChange: (start: string | null, end: string | null) => void;
  eventCandidates: RouteStopCandidate[];
  noteCandidates: RouteStopCandidate[];
  todoCandidates: RouteStopCandidate[];
  eventsLoading: boolean;
  scanStatus: RouteAsyncStatus;
  scanError: string | null;
  onRetryScan: () => void;
  familyMembers: FamilyMember[];
  onToggle: (candidateId: string) => void;
  onTimeChange: (candidateId: string, time: string) => void;
};

function shiftDate(date: string, deltaDays: number): string {
  const parsed = new Date(`${date}T00:00:00`);
  parsed.setDate(parsed.getDate() + deltaDays);
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
}

function formatDateLabel(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * StopPickerStep — wizard step 1: pick which stops matter.
 * Date + time window selectors and three checkbox groups: calendar events
 * (instant), note-scanned obligations (async), and open to-dos.
 */
export function StopPickerStep({
  date,
  onDateChange,
  windowStart,
  windowEnd,
  onWindowChange,
  eventCandidates,
  noteCandidates,
  todoCandidates,
  eventsLoading,
  scanStatus,
  scanError,
  onRetryScan,
  familyMembers,
  onToggle,
  onTimeChange,
}: StopPickerStepProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

  const handleDatePicked = useCallback(
    (result: { date?: string }) => {
      setIsDatePickerVisible(false);
      if (result.date) {
        onDateChange(result.date);
      }
    },
    [onDateChange],
  );

  const membersById = useMemo(
    () => new Map(familyMembers.map((member) => [member.id, member])),
    [familyMembers],
  );

  const renderCheckbox = useCallback(
    (selected: boolean) => (
      <Ionicons
        name={selected ? 'checkbox' : 'square-outline'}
        size={22}
        color={selected ? palette.primary : palette.textMuted}
      />
    ),
    [palette],
  );

  const renderEventRow = (candidate: RouteStopCandidate) => (
    <TouchableOpacity
      key={candidate.id}
      style={[styles.row, { backgroundColor: palette.cardBackground }]}
      onPress={() => onToggle(candidate.id)}
      activeOpacity={0.7}
    >
      {renderCheckbox(candidate.selected)}
      <View style={styles.rowBody}>
        <Text style={[styles.rowTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
          {candidate.fixedStart ? `${candidate.fixedStart}  ` : ''}
          {candidate.title}
        </Text>
        <Text style={[styles.rowSubtitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {candidate.location || 'no location yet'}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const renderNoteRow = (candidate: RouteStopCandidate) => {
    const member = candidate.familyMemberIds[0]
      ? membersById.get(candidate.familyMemberIds[0])
      : undefined;
    return (
      <TouchableOpacity
        key={candidate.id}
        style={[styles.row, { backgroundColor: palette.cardBackground }]}
        onPress={() => onToggle(candidate.id)}
        activeOpacity={0.7}
      >
        {renderCheckbox(candidate.selected)}
        <View style={styles.rowBody}>
          <View style={styles.noteHeader}>
            {member && (
              <SimpleAvatar
                name={member.name}
                color={member.color}
                size={22}
                imageUri={member.profilePicture}
              />
            )}
            <Text style={[styles.rowTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
              {candidate.title}
            </Text>
            <TextInput
              style={[
                styles.timeChip,
                {
                  color: palette.text,
                  borderColor: palette.border,
                  fontFamily: getBodyFont(),
                },
              ]}
              defaultValue={candidate.fixedStart ?? ''}
              placeholder="HH:mm"
              placeholderTextColor={palette.textMuted}
              maxLength={5}
              onEndEditing={(event) => {
                const value = event.nativeEvent.text.trim();
                if (/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
                  onTimeChange(candidate.id, value);
                }
              }}
            />
          </View>
          {candidate.sourceQuote && (
            <Text
              style={[styles.quote, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={2}
            >
              {`“${candidate.sourceQuote}”`}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderTodoRow = (candidate: RouteStopCandidate) => (
    <TouchableOpacity
      key={candidate.id}
      style={[styles.row, { backgroundColor: palette.cardBackground }]}
      onPress={() => onToggle(candidate.id)}
      activeOpacity={0.7}
    >
      {renderCheckbox(candidate.selected)}
      <View style={styles.rowBody}>
        <Text style={[styles.rowTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
          {candidate.title}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const selectedWindow = TIME_WINDOWS.find(
    (option) => option.start === windowStart && option.end === windowEnd,
  );

  return (
    <View style={styles.container}>
      {/* Date selector */}
      <View style={styles.dateRow}>
        <TouchableOpacity onPress={() => onDateChange(shiftDate(date, -1))} hitSlop={8}>
          <Ionicons name="chevron-back" size={22} color={palette.primary} />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.dateLabelButton}
          onPress={() => setIsDatePickerVisible(true)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Choose a date"
        >
          <Text style={[styles.dateLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
            {formatDateLabel(date)}
          </Text>
          <Ionicons name="chevron-down" size={16} color={palette.textMuted} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => onDateChange(shiftDate(date, 1))} hitSlop={8}>
          <Ionicons name="chevron-forward" size={22} color={palette.primary} />
        </TouchableOpacity>
      </View>

      {/* Time window chips */}
      <View style={styles.windowRow}>
        {TIME_WINDOWS.map((option) => {
          const isActive = option === selectedWindow;
          return (
            <TouchableOpacity
              key={option.label}
              style={[
                styles.windowChip,
                {
                  backgroundColor: isActive ? palette.primary : palette.cardBackground,
                  borderColor: isActive ? palette.primary : palette.border,
                },
              ]}
              onPress={() => onWindowChange(option.start, option.end)}
            >
              <Text
                style={[
                  styles.windowChipLabel,
                  { color: isActive ? '#fff' : palette.textMuted, fontFamily: getBodyFont() },
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Your events */}
      <Text style={[styles.groupTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
        Your events
      </Text>
      {eventsLoading ? (
        <ActivityIndicator color={palette.primary} style={styles.loader} />
      ) : eventCandidates.length === 0 ? (
        <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          No events in this window.
        </Text>
      ) : (
        eventCandidates.map(renderEventRow)
      )}

      {/* Found in family notes — hidden when the scan returns nothing */}
      {(scanStatus === 'loading' || scanStatus === 'error' || noteCandidates.length > 0) && (
        <>
          <Text style={[styles.groupTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
            Found in family notes
          </Text>
          {scanStatus === 'loading' && <ActivityIndicator color={palette.primary} style={styles.loader} />}
          {scanStatus === 'error' && (
            <View style={styles.errorRow}>
              <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                {scanError ?? 'Could not scan family notes.'}
              </Text>
              <TouchableOpacity onPress={onRetryScan}>
                <Text style={[styles.retryText, { color: palette.primary, fontFamily: getBodyFont() }]}>
                  Retry
                </Text>
              </TouchableOpacity>
            </View>
          )}
          {scanStatus === 'done' && noteCandidates.map(renderNoteRow)}
        </>
      )}

      {/* Your to-dos */}
      {todoCandidates.length > 0 && (
        <>
          <Text style={[styles.groupTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
            Your to-dos
          </Text>
          {todoCandidates.map(renderTodoRow)}
        </>
      )}

      <DateTimePickerModal
        visible={isDatePickerVisible}
        mode="date"
        selectedDate={date}
        selectedTime={null}
        accentColor={palette.primary}
        onConfirm={handleDatePicked}
        onClose={() => setIsDatePickerVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.sm,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.xl,
    paddingVertical: Spacing.sm,
  },
  dateLabelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
  },
  dateLabel: {
    fontSize: 16,
    fontWeight: '600',
    minWidth: 140,
    textAlign: 'center',
  },
  windowRow: {
    flexDirection: 'row',
    gap: Spacing.sm,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  windowChip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: BorderRadius.xxl,
    borderWidth: 1,
  },
  windowChipLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  groupTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: Spacing.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.md,
    padding: Spacing.md,
    borderRadius: BorderRadius.md,
  },
  rowBody: {
    flex: 1,
    gap: Spacing.xxs,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: '500',
    flexShrink: 1,
  },
  rowSubtitle: {
    fontSize: 12,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  timeChip: {
    marginLeft: 'auto',
    borderWidth: 1,
    borderRadius: BorderRadius.sm,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    fontSize: 13,
    minWidth: 58,
    textAlign: 'center',
  },
  quote: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  loader: {
    paddingVertical: Spacing.md,
  },
  emptyText: {
    fontSize: 13,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  retryText: {
    fontSize: 13,
    fontWeight: '600',
  },
});
